import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  addDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onSnapshot,  // Added for real-time updates
  startAt,     // Added for search functionality
  endAt        // Added for search functionality
} from "firebase/firestore";
import { getDatabase, ref, query as rtdbQuery, orderByChild, equalTo, get, remove , set as rtdbSet} from "firebase/database";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const database = getDatabase(app);
const analyticsPromise = isAnalyticsSupported().then((supported) => (
  supported && firebaseConfig.measurementId ? getAnalytics(app) : null
));

// Authentication functions
const signUp = async (email, password, displayName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    await setDoc(doc(db, "users", userCredential.user.uid), {
      displayName,
      email,
      createdAt: new Date(),
      profilePhotoUrl: "",
      name: displayName
    });
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const userRef = doc(db, "users", result.user.uid);
    
    if (!(await getDoc(userRef)).exists()) {
      await setDoc(userRef, {
        displayName: result.user.displayName,
        email: result.user.email,
        photoURL: result.user.photoURL,
        profilePhotoUrl: result.user.photoURL || "",
        name: result.user.displayName,
        createdAt: new Date()
      });
    }
    return result.user;
  } catch (error) {
    throw error;
  }
};

const logout = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    throw error;
  }
};

// Export all needed functionality
export {
  auth,
  db,
  database,
  analyticsPromise,
  onAuthStateChanged,
  signUp,
  signIn,
  signInWithGoogle,
  logout,
  // Firestore functions
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  rtdbSet as set ,
  addDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onSnapshot,  // Export for real-time updates
  startAt,     // Export for search functionality
  endAt,       // Export for search functionality
  // Realtime Database functions
  ref,
  rtdbQuery,
  orderByChild,
  equalTo,
  get,
  remove
};

export default app;
